/* eslint-disable */
import useAppState from "@saleor/hooks/useAppState";
import { defaultDataIdFromObject, InMemoryCache } from "apollo-cache-inmemory";
import { ApolloClient } from "apollo-client";
import { ApolloLink } from "apollo-link";
import { BatchHttpLink } from "apollo-link-batch-http";
import { createUploadLink } from "apollo-upload-client";
import React from "react";
import { ApolloProvider } from "react-apollo";
import { render } from "react-dom";
import ErrorBoundary from "react-error-boundary";
// import TagManager from "react-gtm-module";
import { useIntl } from "react-intl";
import { BrowserRouter, Route, Switch } from "react-router-dom";

import AppsSection from "./apps";
import { appsSection } from "./apps/urls";
import AttributeSection from "./attributes";
import { attributeSection } from "./attributes/urls";
import Auth from "./auth";
import AuthProvider, { useAuth } from "./auth/AuthProvider";
import LoginLoading from "./auth/components/LoginLoading/LoginLoading";
import SectionRoute from "./auth/components/SectionRoute";
import authLink from "./auth/link";
import { hasPermission } from "./auth/misc";
import CategorySection from "./categories";
import CollectionSection from "./collections";
import AppLayout from "./components/AppLayout";
import { AppShopApiProvider } from "./components/AppLayout/AppShopApiContext";
import { DateProvider } from "./components/Date";
import { LocaleProvider } from "./components/Locale";
import MessageManagerProvider from "./components/messages";
import { ShopProvider } from "./components/Shop";
import ThemeProvider from "./components/Theme";
import { WindowTitle } from "./components/WindowTitle";
import {
  API_URI,
  APP_MOUNT_URI,
  getShopAPI,
  // GTM_ID,
  setShopAPI
} from "./config";
import ConfigurationSection, { createConfigurationMenu } from "./configuration";
import AppStateProvider from "./containers/AppState";
import BackgroundTasksProvider from "./containers/BackgroundTasks";
import CornermarkSection from "./cornermarks";
import PageheaderSection from "./pageheaders";
import CountrySection from "./countries";
import CurrencyrateSection from "./currencyrate";
import { CustomerSection } from "./customers";
import DiscountSection from "./discounts";
import HomePage from "./home";
import HotsearchSection from "./hotsearch";
import InslabelSection from "./inslabel";
import InstagramSection from "./instagram";
import { commonMessages } from "./intl";
import PopupSection from "./popup";
import RegisterSection from "./register";
import ProducttagsSection from "./producttags";
import NavigationSection from "./navigation";
import { navigationSection } from "./navigation/urls";
import { NotFound } from "./NotFound";
import OrdersSection from "./orders";
import pageManage from "./pageManage";
import { pageEventSection, pageManageSection } from "./pageManage/urls";
import PageSection from "./pages";
import SiteLanguageSection from "./siteLanguages";
import PermissionGroupSection from "./permissionGroups";
import PermissionShopSection from "./permissionSite";
import PluginsSection from "./plugins";
import ProductSection from "./products";
import SoldoutCustomPageSection from "./soldoutcustompage";
import ProductTypesSection from "./productTypes";
import { RotarySection } from "./rotary";
import { ShipmentSection } from "./shipment";
import ShippingSection from "./shipping";
import SiteSettingsSection from "./siteSettings";
import StaffSection from "./staff";
import StaffmyselfSection from "./staffmyself";
import SubscriberSection from "./subscribers";
import TaxesSection from "./taxes";
import TranslationsSection from "./translations";
import { PermissionEnum } from "./types/globalTypes";
import WarehouseSection from "./warehouses";
import { warehouseSection } from "./warehouses/urls";
import CoverSection from "./cover";
import { emailTemplateSection } from "./emailTemplate";
import RecommendPopupSection from "./recommend";
import SpecialOfferSection from "./specialOffer";
import { ErpStatusNoteSection } from "./erpstatusnote";
import { ComboSection } from "./combos";
import InsCommentSection from "./inscomment";
import CheckoutAddSection from "./checkoutAdd";
import { ProductActivationSettingSection } from "./productActivationSetting";
import { ProductLensTypeSection } from "./productLensType";
// 谷歌跟踪代码
// if (process.env.GTM_ID !== undefined) {
//   TagManager.initialize({ gtmId: GTM_ID });
// }
// 默认店铺接口,通过setShopAPI来切换店铺，设置API_URI为第一个店铺接口
if (getShopAPI().api_uri === null && API_URI !== undefined) {
  setShopAPI(API_URI);
}

// DON'T TOUCH THIS
// These are separate clients and do not share configs between themselves
// so we need to explicitly set them
const linkOptions = {
  credentials: "include",
  uri: getShopAPI().api_uri
};
const uploadLink = createUploadLink(linkOptions);
const batchLink = new BatchHttpLink({
  batchInterval: 100,
  ...linkOptions
});

const link = ApolloLink.split(
  operation => operation.getContext().useBatching,
  batchLink,
  uploadLink
);

// apollo-client
// credentials: "include"  允许跨域   'same-origin'只能同域
// batchInterval 允许网络层查询合并的时间
// authLink 在header添加了Authorization的jwt验证
// 请求TokenAuth成功后 Set-Cookie:refreshToken
// 请求loginByToken成功后 localStorage/sessionStorage：auth,csrf
const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    dataIdFromObject: (obj: any) => {
      // We need to set manually shop's ID, since it is singleton and
      // API does not return its ID
      if (obj.__typename === "Shop") {
        return "shop";
      }
      return defaultDataIdFromObject(obj);
    }
  }),
  link: authLink.concat(link)
});

const App: React.FC = () => {
  const isDark = localStorage.getItem("theme") === "true";

  return (
    <ApolloProvider client={apolloClient}>
      <BrowserRouter basename={APP_MOUNT_URI}>
        <ThemeProvider isDefaultDark={isDark}>
          <DateProvider>
            <LocaleProvider>
              <MessageManagerProvider>
                <BackgroundTasksProvider>
                  <AppStateProvider>
                    <ShopProvider>
                      <AuthProvider>
                        <AppShopApiProvider>
                          <Routes />
                        </AppShopApiProvider>
                      </AuthProvider>
                    </ShopProvider>
                  </AppStateProvider>
                </BackgroundTasksProvider>
              </MessageManagerProvider>
            </LocaleProvider>
          </DateProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ApolloProvider>
  );
};

const Routes: React.FC = () => {
  const intl = useIntl();
  const [, dispatchAppState] = useAppState();
  const {
    hasToken,
    isAuthenticated,
    tokenAuthLoading,
    tokenVerifyLoading,
    user
  } = useAuth();

  return (
    <>
      <WindowTitle title={intl.formatMessage(commonMessages.dashboard)} />
      {isAuthenticated && !tokenAuthLoading && !tokenVerifyLoading ? (
        <AppLayout>
          <ErrorBoundary
            onError={() =>
              dispatchAppState({
                payload: {
                  error: "unhandled"
                },
                type: "displayError"
              })
            }
          >
            <Switch>
              <SectionRoute exact path="/" component={HomePage} />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PAGES]}
                path={pageManageSection}
                component={pageManage}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PAGES]}
                path={pageEventSection}
                component={pageManage}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_CATEGORY]}
                path="/categories"
                component={CategorySection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_RECOMMENDATION_GROUP]}
                path="/collections"
                component={CollectionSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_INSTAGRAM]}
                path="/instagram"
                component={InstagramSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_INSTAGRAM]}
                path="/inscomment"
                component={InsCommentSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_COUPON_CODE]}
                path="/combos"
                component={ComboSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_INSTAGRAM_LABEL]}
                path="/inslabel"
                component={InslabelSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_SHIPMENT]}
                path="/shipment"
                component={ShipmentSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_SHIPMENT]}
                path="/statusnote"
                component={ErpStatusNoteSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_USERS]}
                path="/customers"
                component={CustomerSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_COVER]}
                path="/cover"
                component={CoverSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_SALES]}
                path="/discounts"
                component={DiscountSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PAGES]}
                path="/pages"
                component={PageSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PAGES]}
                path="/pageheaders"
                component={PageheaderSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PLUGINS]}
                path="/plugins"
                component={PluginsSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PAYMENT]}
                path="/pluginspayment"
                component={PluginsSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_ORDERS]}
                path="/orders"
                component={OrdersSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                path="/products"
                component={ProductSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PAGES]}
                path="/soldouts"
                component={SoldoutCustomPageSection}
              />
              <SectionRoute
                permissions={[
                  PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES
                ]}
                path="/product-types"
                component={ProductTypesSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_STAFF]}
                path="/staff"
                component={StaffSection}
              />
              <SectionRoute
                // permissions={[PermissionEnum.MANAGE_POPUP]}
                path="/staff-myself"
                component={StaffmyselfSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_STAFF]}
                path="/shop-staff"
                component={PermissionShopSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_STAFF]}
                path="/permission-groups"
                component={PermissionGroupSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_SETTINGS]}
                path="/site-settings"
                component={SiteSettingsSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_SETTINGS]}
                path="/site-languages"
                component={SiteLanguageSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_SETTINGS]}
                path="/taxes"
                component={TaxesSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_CURRENCYRATE]}
                path="/currencyrate"
                component={CurrencyrateSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_CORNERMARK]}
                path="/cornermark"
                component={CornermarkSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_COUNTRY]}
                path="/country"
                component={CountrySection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_ROTARY]}
                path="/rotary"
                component={RotarySection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_POPUP]}
                path="/recommendPopup"
                component={RecommendPopupSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_REDEMPTIONS]}
                path="/specialOffer"
                component={SpecialOfferSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_ROTARY]}
                path="/emailTemplate"
                component={emailTemplateSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_HOTSEARCH]}
                path="/hotsearch"
                component={HotsearchSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_POPUP]}
                path="/popup"
                component={PopupSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_POPUP]}
                path="/register"
                component={RegisterSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PRODUCTTAGS]}
                path="/producttags"
                component={ProducttagsSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_SHIPPING]}
                path="/shipping"
                component={ShippingSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_TRANSLATIONS]}
                path="/translations"
                component={TranslationsSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_MENUS]}
                path={navigationSection}
                component={NavigationSection}
              />
              <SectionRoute
                permissions={[
                  PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES
                ]}
                path={attributeSection}
                component={AttributeSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_APPS]}
                path={appsSection}
                component={AppsSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_WAREHOUSE]}
                path={warehouseSection}
                component={WarehouseSection}
              />
              {createConfigurationMenu(intl).filter(menu =>
                menu.menuItems.map(item => hasPermission(item.permission, user))
              ).length > 0 && (
                <SectionRoute
                  exact
                  path="/configuration"
                  component={ConfigurationSection}
                />
              )}
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_SUBSCRIPTION]}
                path="/subscriber"
                component={SubscriberSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PLUGINS]}
                path="/checkoutAdd"
                component={CheckoutAddSection}
              />

              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PRODUCTACTIVATION]}
                path="/productActivationSetting"
                component={ProductActivationSettingSection}
              />
              <SectionRoute
                permissions={[PermissionEnum.MANAGE_PRODUCTLENSTYPE]}
                path="/productLensType"
                component={ProductLensTypeSection}
              />
              <Route component={NotFound} />
            </Switch>
          </ErrorBoundary>
        </AppLayout>
      ) : hasToken && tokenVerifyLoading ? (
        <LoginLoading />
      ) : (
        <Auth />
      )}
    </>
  );
};

render(<App />, document.querySelector("#dashboard-app"));
